import {memo, useCallback, useMemo, useState} from 'react';

import {Box, Popover, Typography} from '@mui/material';
import {DateCalendar} from '@mui/x-date-pickers/DateCalendar';
import {format as formatDate} from 'date-fns';

import './styles';

export interface DatePickerInputProps {
  id?: string;
  label: string;
  value: Date | null;
  onChange: (date: Date | null) => void;
  minDate?: Date;
  maxDate?: Date;
  disabled?: boolean;
  /**
   * date-fns format string used to render the selected value.
   * Defaults to `'MMM d, yyyy'` (en-US, e.g. "May 6, 2024").
   */
  format?: string;
  /** Placeholder shown when no date has been selected. */
  placeholder?: string;
}

const DEFAULT_FORMAT = 'MMM d, yyyy';

const popoverAnchorOrigin = {vertical: 'bottom', horizontal: 'left'} as const;
const popoverTransformOrigin = {vertical: 'top', horizontal: 'left'} as const;

/**
 * Stacked-label text-field trigger that opens a MUI `DateCalendar` popover.
 * The selected value is displayed using a date-fns format string (defaults
 * to the en-US `MMM d, yyyy`). The neutral state has a 1px gray outline;
 * focused/open state has a 2px green outline and accent label color.
 */
const DatePickerInput: React.FC<DatePickerInputProps> = ({
  id,
  label,
  value,
  onChange,
  minDate,
  maxDate,
  disabled = false,
  format = DEFAULT_FORMAT,
  placeholder = 'Select a date',
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = !!anchorEl;

  const formattedValue = useMemo(() => {
    if (!value) return '';

    try {
      return formatDate(value, format);
    } catch {
      return '';
    }
  }, [value, format]);

  const handleOpen = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (disabled) return;
      setAnchorEl(e.currentTarget);
    },
    [disabled],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (disabled) return;
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setAnchorEl(e.currentTarget);
      }
    },
    [disabled],
  );

  const handleClose = useCallback(() => setAnchorEl(null), []);

  const handleChange = useCallback(
    (date: Date | null) => {
      onChange(date);
      setAnchorEl(null);
    },
    [onChange],
  );

  const popoverSlotProps = useMemo(
    () => ({
      paper: {
        className: 'date-picker-input-popover-paper',
      },
    }),
    [],
  );

  const showPlaceholder = !formattedValue;
  const triggerClassName = [
    'date-picker-input',
    open ? 'open' : '',
    disabled ? 'disabled' : '',
  ]
    .filter(Boolean)
    .join(' ');
  const valueClassName = [
    'date-picker-input-value',
    showPlaceholder ? 'placeholder' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <>
      <Box
        id={id}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-disabled={disabled}
        aria-label={label}
        onClick={handleOpen}
        onKeyDown={handleKeyDown}
        className={triggerClassName}>
        <Box className="date-picker-input-text">
          <Typography className="date-picker-input-label">{label}</Typography>

          <Typography className={valueClassName}>
            {formattedValue || placeholder}
          </Typography>
        </Box>
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={popoverAnchorOrigin}
        transformOrigin={popoverTransformOrigin}
        slotProps={popoverSlotProps}>
        <Box className="date-picker-input-calendar">
          <DateCalendar
            value={value}
            onChange={handleChange}
            minDate={minDate}
            maxDate={maxDate}
          />
        </Box>
      </Popover>
    </>
  );
};

export default memo(DatePickerInput);
